'use client';

import React, { useState } from 'react';

import { cn } from '../helpers/cn';

export type SwitchProps = {
    className?: string;
    state?: boolean;
    disabled?: boolean;
    defaultText?: string;
    offText?: string;
    onText?: string;
    onChange?: (state: boolean) => void;
    onColour?: string;
    offColour?: string;
    switchColour?: string;
    stateTextLeft?: boolean;
    /** 'small' renders a compact 36×22px track, e.g. for dense image grids. */
    size?: 'default' | 'small';
    /** id of the element labelling this switch, wired by the labelled wrappers. */
    ariaLabelledby?: string;
    /** Accessible name used when no visible label element is associated. */
    ariaLabel?: string;
    /** id(s) of the element(s) explaining the switch, e.g. why it is disabled. */
    ariaDescribedby?: string;
    /** Shows only `state`: a click asks the parent through `onChange`, so a parent that says no keeps the switch off. */
    controlled?: boolean;
    /** Replaces the default focus ring, for surfaces that have no `--primary-color-rgb` (the studios). */
    focusRingClass?: string;
};

function Switch(props: SwitchProps): React.JSX.Element {
    const {
        className,
        state,
        disabled,
        defaultText,
        offText,
        onText,
        onChange,
        onColour,
        offColour,
        switchColour,
        stateTextLeft,
        size = 'default',
        ariaLabelledby,
        ariaLabel,
        ariaDescribedby,
        controlled,
        focusRingClass
    } = props;

    const [switchState, setSwitchState] = useState<boolean>(state || false);
    // The switch keeps its own state so an uncontrolled caller still gets a working toggle, but a
    // controlled `state` prop has to win when the parent changes it. That sync happens during
    // render rather than in an effect: an effect commits the stale position to the DOM first and
    // then cascades a second render, whereas this discards the in-progress render before paint.
    const [lastStateProp, setLastStateProp] = useState<boolean | undefined>(state);
    if (state !== lastStateProp) {
        setLastStateProp(state);
        setSwitchState(state ?? false);
    }

    const _defaultText = defaultText || '';
    const _offText = offText || '';
    const _onText = onText || '';
    const _onColour = onColour || 'bg-darkBlue';
    const _offColour = offColour || 'bg-gray-light';
    const _switchColour = switchColour || 'bg-white';

    function toggleSwitch() {
        if (!disabled) {
            const newState = !switchState;
            if (!controlled) setSwitchState(newState);
            if (onChange && typeof onChange === 'function') {
                onChange(newState);
            }
        }
    }

    return (
        <div
            className={`flex flex-row gap-8 items-center ${className || ''} ${
                stateTextLeft ? 'flex-row-reverse' : ''
            }`}>
            <button
                type="button"
                role="switch"
                aria-checked={switchState}
                aria-labelledby={ariaLabelledby}
                aria-label={ariaLabelledby ? undefined : ariaLabel}
                aria-describedby={ariaDescribedby}
                aria-disabled={disabled || undefined}
                className={cn(
                    'relative flex flex-none rounded-[9999px] z-0 overflow- p-[2px]',
                    size === 'small' ? 'w-[36px] h-[22px]' : 'w-[52px] h-[32px]',
                    'focus:outline-none focus:ring-0',
                    // A finger gets 44 px of height around the track (the studio marks itself as coarse).
                    "group-data-[coarse=true]/studio:after:absolute group-data-[coarse=true]/studio:after:-inset-x-4 group-data-[coarse=true]/studio:after:-inset-y-12 group-data-[coarse=true]/studio:after:content-['']",
                    'transition-all duration-200',
                    disabled ? 'cursor-default' : 'cursor-pointer',
                    switchState ? _onColour : _offColour,
                    focusRingClass ??
                        (switchState
                            ? // primary at 50% if the domain theme sets --primary-color-rgb,
                              // otherwise fall back to cyan (#00FFDC = 0,255,220), i.e. ring-cyan/50
                              'focus:ring-1 focus:ring-[rgba(var(--primary-color-rgb,0,255,220),0.5)]'
                            : 'focus:ring-1 focus:ring-gray-white')
                )}
                onClick={(e) => {
                    e.preventDefault();
                    toggleSwitch();
                }}>
                <span
                    className={cn(
                        size === 'small' ? 'size-[18px]' : 'size-[28px]',
                        'relative',
                        'shadow-darkBlue-20 rounded-full',
                        'transition-all duration-200 ease-in-out',
                        _switchColour
                    )}
                    style={{
                        left: switchState ? `calc(100% - ${size === 'small' ? 18 : 28}px)` : '0px'
                    }}
                />
            </button>
            {/* Rendered only when there IS text — an empty span would still consume the wrapper gap */}
            {(_defaultText || _offText || _onText) && (
                <span className="inline-block justify-center select-none">
                    {switchState ? _onText || _defaultText : _offText || _defaultText}
                </span>
            )}
        </div>
    );
}

export default Switch;
